import { limitedColors } from "@/components/emoji/limited-colors.ts";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { BarChart } from "@/components/reporting/BarChart.tsx";
import { ChartLegend } from "@/components/reporting/ChartLegend.tsx";
import { LineChart } from "@/components/reporting/LineChart.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import {
  PeopleAnalyticsChartType,
  TOTAL_EMPLOYEES_SERIES_ID,
} from "@/pages/reporting/employee/people-analytics/people-analytics.types.ts";
import {
  ApiPeopleAnalyticsColumn,
  ApiPeopleAnalyticsRows,
} from "@/types/reporting/people-analytics.types.ts";
import { colorCssValues, colorCssVariables, Skeleton, Stack, Text } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

interface PeopleAnalyticsChartProps {
  rows: ApiPeopleAnalyticsRows | undefined;
  chartType: PeopleAnalyticsChartType;
  chartableColumns: ApiPeopleAnalyticsColumn[];
  moneyColumnIds: string[];
  selectedColumnIds: string[];
  isLoading?: boolean;
  onToggleSeries: (columnId: string) => void;
  onPointClick?: (pointIndex: number) => void;
}

export function PeopleAnalyticsChart({
  rows,
  chartType,
  chartableColumns,
  moneyColumnIds,
  selectedColumnIds,
  isLoading,
  onToggleSeries,
  onPointClick,
}: PeopleAnalyticsChartProps) {
  const { t } = useTranslation();
  const { renderCurrency } = useFormatter();

  if (isLoading) {
    return (
      <Stack className="rounded-xs border border-dark/10 px-14 py-10">
        <Skeleton className="w-full" height={360} />
      </Stack>
    );
  }

  const responseColumnIds = (rows?.columns ?? []).map((column) => column.id);
  const labels = (rows?.rows ?? []).map(
    (row) => row.group_label || t("reporting:people_analytics_group_empty")
  );

  const seriesOptions = [
    { id: TOTAL_EMPLOYEES_SERIES_ID, label: t("reporting:people_analytics_total_employees") },
    ...chartableColumns.map((column) => ({ id: column.id, label: column.title })),
  ].map((option, index) => ({
    ...option,
    colorName: limitedColors[index % limitedColors.length],
  }));

  const series = seriesOptions
    .filter((option) => selectedColumnIds.includes(option.id))
    .map((option) => {
      const color = colorCssVariables[option.colorName];
      if (option.id === TOTAL_EMPLOYEES_SERIES_ID) {
        return {
          label: option.label,
          data: (rows?.rows ?? []).map((row) => row.employee_ids.length),
          color,
        };
      }
      const valueIndex = responseColumnIds.indexOf(option.id);
      if (valueIndex < 0) return null;
      return {
        label: option.label,
        data: (rows?.rows ?? []).map((row) => Number(row.values[valueIndex] ?? 0)),
        color,
        formatValue: moneyColumnIds.includes(option.id)
          ? (value: number) => renderCurrency(String(value), rows?.currency ?? null)
          : undefined,
      };
    })
    .filter((entry) => entry !== null);
  const formatAxisValue = series.every((entry) => entry.formatValue)
    ? series[0]?.formatValue
    : undefined;

  if (!rows?.group_by_column) {
    return (
      <Stack className="py-10" items="center">
        <Text className="text-secondary">{t("reporting:people_analytics_chart_needs_group")}</Text>
      </Stack>
    );
  }

  if (series.length === 0) {
    return (
      <Stack className="py-10" items="center">
        <Text className="text-secondary">{t("reporting:people_analytics_chart_needs_series")}</Text>
      </Stack>
    );
  }

  if (labels.length === 0) {
    return <EmptyStateNothingFound />;
  }

  return (
    <Stack className="rounded-xs border border-dark/10 px-14 py-10" gap="md">
      {chartType === PeopleAnalyticsChartType.line ? (
        <LineChart
          labels={labels}
          series={series}
          height={360}
          formatAxisValue={formatAxisValue}
          onPointClick={
            onPointClick ? (_seriesIndex, pointIndex) => onPointClick(pointIndex) : undefined
          }
        />
      ) : (
        <BarChart
          labels={labels}
          series={series}
          height={360}
          showLegend={false}
          onBarClick={
            onPointClick ? (_seriesIndex, pointIndex) => onPointClick(pointIndex) : undefined
          }
        />
      )}
      <ChartLegend
        className={"px-6"}
        items={seriesOptions.map((option) => ({
          id: option.id,
          label: option.label,
          color: colorCssValues[option.colorName],
        }))}
        selectedIds={selectedColumnIds}
        onToggle={onToggleSeries}
      />
    </Stack>
  );
}
